import React, { useMemo, useState } from 'react';
import { Badge, Button, Empty, Popover, Spin, Tooltip, Typography, message } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useKPIConsecutiveFailScan } from '@/hooks/useKPIConsecutiveFailScan';
import { usePrivateStore } from '@/store/usePrivateStore';
import { kpiConsecutiveFailApi } from '@/services/kpi-consecutive-fail/kpi-consecutive-fail-api';
import type { KPIConsecutiveFailAlertItem } from '@/types/kpi-consecutive-fail';
import { KPI_CONSECUTIVE_FAIL_PATH } from '@/utils/kpiConsecutiveFail';

interface Props {
  enabled: boolean;
}

function reasonLabel(t: (k: string, d: string) => string, reason?: string) {
  if (reason === 'overdue_unreported') {
    return t('bsc.msg.kpi_fail_overdue', 'Quá hạn chưa báo cáo');
  }
  return t('bsc.msg.kpi_fail_performance', 'Không đạt hiệu suất');
}

/**
 * Icon cảnh báo riêng trên Header (cùng pattern Buoc6ChiTieuWarningHeaderNotify),
 * không gắn vào chuông NotifyDrawer.
 */
export const KPIConsecutiveFailHeaderNotify: React.FC<Props> = ({ enabled }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const { data, activeCount, loading, scan, flatAlerts, featureEnabled } =
    useKPIConsecutiveFailScan(enabled);
  const departmentLabel = usePrivateStore((s) => s.treeSelectDepartment.selectedValue?.label || '');
  const isFailReportPage =
    typeof router.pathname === 'string' && router.pathname.includes('thong-ke-kpi-khong-dat');

  const handleDismiss = async (item: KPIConsecutiveFailAlertItem) => {
    if (!item.id) return;
    setDismissingId(item.id);
    try {
      const ok = await kpiConsecutiveFailApi.dismiss({
        id: item.id,
        lyDo: t('bsc.msg.dismiss_canhbao_kpi', 'Người dùng bỏ qua cảnh báo'),
      });
      if (ok) {
        message.success(t('bsc.msg.dabocanhbao', 'Đã bỏ qua cảnh báo'));
        await scan({ force: true, sendNotification: false });
      } else {
        message.error(t('bsc.msg.bocanhbaothatbai', 'Bỏ qua cảnh báo thất bại'));
      }
    } catch {
      message.error(t('bsc.msg.bocanhbaothatbai', 'Bỏ qua cảnh báo thất bại'));
    } finally {
      setDismissingId(null);
    }
  };

  const allAlerts = useMemo(() => {
    if (flatAlerts?.length) return flatAlerts;
    if (!data) return [];
    return [
      ...data.self,
      ...data.employees.flatMap((g) => g.alerts),
      ...data.childDepts.flatMap((g) => g.alerts),
    ];
  }, [data, flatAlerts]);

  const popoverContent = useMemo(
    () => (
      <div style={{ width: 320, maxHeight: 360, overflow: 'auto' }}>
        {departmentLabel ? (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, display: 'block', marginBottom: 8, lineHeight: 1.4 }}
          >
            {t('bsc.label.phong_ban_dang_xem', 'Phòng ban đang xem')}:{' '}
            <Typography.Text style={{ fontSize: 12 }}>{departmentLabel}</Typography.Text>
          </Typography.Text>
        ) : null}
        {loading && activeCount === 0 ? (
          <div className="flex justify-center py-6">
            <Spin size="small" />
          </div>
        ) : activeCount === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('bsc.msg.khong_co_canh_bao_3ky', 'Không có cảnh báo 3 kỳ liên tiếp')}
          />
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {allAlerts.map((w) => (
              <li
                key={w.id || `${w.idChiTieu}-${w.kyKichHoat}`}
                style={{
                  padding: '8px 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <Typography.Text strong style={{ fontSize: 13 }}>
                  {w.tenKPI || w.idChiTieu}
                </Typography.Text>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {w.tenPhuTrach ? `${w.tenPhuTrach} · ` : ''}
                  {w.streak} {t('bsc.label.ky_lien_tiep', 'kỳ liên tiếp')}: {w.kyBatDau} → {w.kyKetThuc}
                </div>
                {w.periods?.slice(0, 3).map((p, idx) => (
                  <div key={`${p.title}-${idx}`} style={{ fontSize: 11, color: '#595959', paddingLeft: 4 }}>
                    · {p.title}: {reasonLabel(t, p.reason)}
                  </div>
                ))}
                {w.id ? (
                  <div style={{ marginTop: 4, textAlign: 'right' }}>
                    <Button
                      type="link"
                      size="small"
                      danger
                      loading={dismissingId === w.id}
                      onClick={() => void handleDismiss(w)}
                    >
                      {t('bsc.btn.boqua', 'Bỏ qua')}
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {!isFailReportPage && activeCount > 0 && (
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <Button
              type="link"
              size="small"
              onClick={() => {
                setOpen(false);
                void router.push(KPI_CONSECUTIVE_FAIL_PATH);
              }}
            >
              {t('bsc.btn.xem_thong_ke_kpi_khong_dat', 'Xem thống kê KPI không đạt')}
            </Button>
          </div>
        )}
      </div>
    ),
    [activeCount, allAlerts, departmentLabel, dismissingId, isFailReportPage, loading, router, t]
  );

  // Chỉ hiện khi cấu hình 3 kỳ bật + phân hệ BSC + có cảnh báo
  if (!featureEnabled || (!loading && activeCount === 0)) {
    return null;
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      title={
        departmentLabel
          ? `${t('bsc.msg.canh_bao_kpi_3ky', 'Cảnh báo KPI 3 kỳ liên tiếp')} — ${departmentLabel}`
          : t('bsc.msg.canh_bao_kpi_3ky', 'Cảnh báo KPI 3 kỳ liên tiếp')
      }
      content={popoverContent}
    >
      <Tooltip
        title={t(
          'bsc.msg.canh_bao_kpi_3ky_count',
          departmentLabel
            ? '{{count}} KPI không đạt 3 kỳ ({{department}})'
            : 'Có {{count}} cảnh báo KPI không đạt 3 kỳ liên tiếp',
          { count: activeCount, department: departmentLabel }
        )}
        placement="bottom"
      >
        <div className="relative cursor-pointer transition-transform duration-200 hover:scale-110 hover:-translate-y-0.5">
          <Badge count={activeCount} size="small" offset={[-2, 2]}>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF1F0] text-[#CF1322]">
              <WarningOutlined style={{ fontSize: 18 }} />
            </div>
          </Badge>
        </div>
      </Tooltip>
    </Popover>
  );
};
